import * as React from "react";
import styled, { css } from "styled-components";
import { CssRule } from '../../../themes/architecture/css-helpers/css-rule';
import {PropsWithChildren} from "react";

export enum SvgSizeUnit {
    Rem = "rem",
    Em = "em",
    Px = "px",
    Percent = "%"
}

export interface ISvgIconProps extends PropsWithChildren {
    /**
     * The size of the icon.
     * @default 2.4
     */
    size?: number;
    /**
     * if false, the property size represents the width
     * if true, the property size represents the height
     * @default false
     */
    sizeIsHeight?: boolean;
    /**
     * Css unit: rem, em, px, %
     * @default rem
     */
    sizeUnit?: SvgSizeUnit;
    /**
     * Additional css,
     * Do not abuse this prop. You will be punished
     */
    style?: React.CSSProperties;
    /**
     * Additional class name for styling, selectors
     * Do not abuse this prop. You will be punished
     */
    className?: string;
    /**
     * title is required for SEO
     */
    title?: string;
}

interface ISvgIconPrivateProps {
    /**
     * The aspect ratio of the icon. is expressed by the width divided by the height.
     * For example an icon that is 16:9 will have a ratio of 16/9 = 1.(7) ~= 1.78
     * Default is 1, square icon
     * @default 1
     */
    ratio?: number;
    /**
     * Specific svg property
     */
    viewBox?: string;
    /**
     * Some icons needs to be flipped on RTL languages, for example left arrow icon (<) must be flipped, and will look like a right arrow icon (>)
     */
    flipOnRTL?: boolean;
    /**
     * Used to normalize / reset the fill property. Some of the icons have black backdrop opacity without it.
     * alternative to wrapping the whole svg markup content in <g fill="none"></g>. It doesn't matter which one is used
     */
    fill?: string;
}

const Svg = styled.svg<{flipOnRTL?: boolean}>`
    ${({flipOnRTL}) => flipOnRTL ? css`
        ${CssRule.For.RTL.build()} & {
            transform: scaleX(-1);
        }`
    : null}
`;

export class SvgIcon extends React.Component<ISvgIconProps & ISvgIconPrivateProps> {
    render() {
        const sizeUnit = this.props.sizeUnit ? this.props.sizeUnit : SvgSizeUnit.Rem;
        const defaultSize = sizeUnit === SvgSizeUnit.Px ? 24 : (sizeUnit === SvgSizeUnit.Percent ? 100 : 2.4);
        const size = this.props.size ? this.props.size : defaultSize;
        const ratio = this.props.ratio ? this.props.ratio : 1;

        const width = this.props.sizeIsHeight ? (size * ratio) : size;
        const height = this.props.sizeIsHeight ? size : (size / ratio);

        const viewBox = this.props.viewBox ?? "0 0 24 24";
        const style = {
            display: "block",
            ...this.props.style
        };

        return (
            <Svg
                flipOnRTL={this.props.flipOnRTL}
                width={width + sizeUnit} height={height + sizeUnit}
                style={style}
                viewBox={viewBox}
                version="1.1" xmlns="http://www.w3.org/2000/svg"
                className={this.props.className}
                fill={this.props.fill || "unset"}
            >
                {this.props.title ? <title>{this.props.title}</title> : null}
                {this.props.children}
            </Svg>
        );
    }
}
